import { dataFrom } from '@zyno-io/openapi-client-codegen';
import { uuid } from '@zyno-io/vue-foundation';
import { shallowRef } from 'vue';

import { GlobalState } from '@/global';
import { canTransferDeskCall } from '@/modules/talk/helpers/extension-live-calls';
import { TalkCallControlApi, type IExtensionLiveCallResponse, type PostCallControlTransferData } from '@/openapi-clients-generated/talk';
import { apiErrorStatus, EXPECTED_MUTATION_ERRORS } from '@/shared/helpers/api-errors';

import { extensionLiveCallsMgr } from './extension-live-calls.manager';

/** Shorter than Nexus's two-minute replay retention, including the initial HTTP request. */
export const DESK_TRANSFER_REPLAY_MS = 75_000;
const MAX_REMEMBERED_SOURCES = 128;

export type DeskCallSource = Pick<IExtensionLiveCallResponse, 'callId' | 'extensionId'> & { controlLegId: string };
type TransferBody = PostCallControlTransferData['body'];
type TransferStatus = 'sending' | 'accepted' | 'rejected' | 'unknown';

export interface DeskTransferAttempt {
    readonly source: Readonly<DeskCallSource>;
    readonly body: Readonly<TransferBody>;
    readonly startedAtMs: number;
    readonly status: TransferStatus;
    readonly message: string;
}

interface DeskCallTransferDependencies {
    scope: () => string;
    calls: () => readonly IExtensionLiveCallResponse[];
    refresh: () => Promise<boolean>;
    now: () => number;
    operationId: () => string;
}

/**
 * A source's frozen attempt outlives its modal. In particular, dismissing an unknown response must
 * never turn the next open into a new operation. Settled acceptance also blocks that retired source;
 * a newly connected leg has its own key and can transfer immediately. A successful current snapshot
 * retires acknowledged source tombstones once their immutable leg is gone. Unresolved attempts stay.
 */
export class DeskCallTransferManager {
    private readonly attempts = shallowRef<ReadonlyMap<string, DeskTransferAttempt>>(new Map());
    private currentScope = '';

    constructor(
        private readonly deps: DeskCallTransferDependencies = {
            scope: () => `${GlobalState.tenant.value?.id ?? ''}:${GlobalState.user.value?.id ?? ''}:${GlobalState.workstation.value?.id ?? ''}`,
            calls: () => extensionLiveCallsMgr.calls.value,
            refresh: () => extensionLiveCallsMgr.refresh(),
            now: () => Date.now(),
            operationId: uuid
        }
    ) {}

    private key(source: DeskCallSource): string {
        const scope = this.deps.scope();
        if (scope !== this.currentScope) {
            this.currentScope = scope;
            this.attempts.value = new Map();
        }
        return `${scope}:${source.callId}:${source.extensionId}:${source.controlLegId}`;
    }

    attempt(source: DeskCallSource): DeskTransferAttempt | undefined {
        return this.attempts.value.get(this.key(source));
    }

    canReplay(attempt: DeskTransferAttempt, now = this.deps.now()): boolean {
        return attempt.status === 'unknown' && now - attempt.startedAtMs < DESK_TRANSFER_REPLAY_MS;
    }

    private put(key: string, attempt: DeskTransferAttempt) {
        const next = new Map(this.attempts.value);
        next.set(key, Object.freeze(attempt));
        this.attempts.value = next;
    }

    private currentCall(source: DeskCallSource): IExtensionLiveCallResponse | undefined {
        return this.deps
            .calls()
            .find(call => call.callId === source.callId && call.extensionId === source.extensionId && call.controlLegId === source.controlLegId);
    }

    /** Only a successful available snapshot can retire settled source state. */
    async refresh(): Promise<boolean> {
        return this.refreshAndRetire();
    }

    private async refreshAndRetire(preserveKey?: string): Promise<boolean> {
        const scope = this.deps.scope();
        const fresh = await this.deps.refresh();
        if (fresh && scope === this.deps.scope()) {
            this.attempts.value = new Map(
                [...this.attempts.value].filter(
                    ([key, attempt]) =>
                        key === preserveKey || attempt.status === 'unknown' || attempt.status === 'sending' || !!this.currentCall(attempt.source)
                )
            );
        }
        return fresh;
    }

    async transfer(source: DeskCallSource, destinationExtensionId: string, transferReason?: string): Promise<void> {
        const key = this.key(source);
        const previous = this.attempts.value.get(key);
        if (previous && previous.status !== 'rejected') return;
        // Keep unresolved/accepted source tombstones bounded without evicting their replay safety.
        const retained = new Map([...this.attempts.value].filter(([, attempt]) => attempt.status !== 'rejected'));
        this.attempts.value = retained;

        const body: TransferBody = Object.freeze({
            extensionId: source.extensionId,
            legId: source.controlLegId,
            destinationExtensionId,
            operationId: this.deps.operationId(),
            ...(transferReason !== undefined ? { transferReason } : {})
        });
        let attempt: DeskTransferAttempt = {
            source: Object.freeze({ ...source }),
            body,
            startedAtMs: this.deps.now(),
            status: 'sending',
            message: 'Checking this desk call…'
        };
        this.put(key, attempt); // Reserve synchronously, before the refresh or any request can yield.
        const fresh = await this.refresh();
        if (key !== this.key(source)) return;
        const call = this.currentCall(source);
        if (!fresh || !call || !canTransferDeskCall(call)) {
            this.put(key, {
                ...attempt,
                status: 'rejected',
                message: 'This desk call changed or could not be refreshed. Refresh before trying again.'
            });
            return;
        }
        const lockedSources = [...this.attempts.value].filter(([otherKey, other]) => otherKey !== key && other.status !== 'rejected');
        if (lockedSources.length >= MAX_REMEMBERED_SOURCES) {
            this.put(key, {
                ...attempt,
                status: 'rejected',
                message: 'This browser has too many unresolved desk calls. Use the desk phone for this call.'
            });
            return;
        }
        attempt = { ...attempt, startedAtMs: this.deps.now(), message: 'Requesting transfer…' };
        this.put(key, attempt);
        await this.dispatch(key, attempt, false);
    }

    async replay(source: DeskCallSource): Promise<void> {
        const key = this.key(source);
        const attempt = this.attempts.value.get(key);
        if (!attempt || !this.canReplay(attempt)) return;
        this.put(key, { ...attempt, status: 'sending', message: 'Checking the same transfer request…' });
        // Replay intentionally does not require a still-connected leg or a still-listed target.
        // Nexus retains proof after source retirement; Talk reauthorizes the active extension.
        await this.dispatch(key, attempt, true);
    }

    private async dispatch(key: string, attempt: DeskTransferAttempt, replay: boolean): Promise<void> {
        let status: TransferStatus = 'unknown';
        let message = 'The transfer response was lost. Only the same request can be checked safely.';
        try {
            const response = await TalkCallControlApi.postCallControlTransfer({
                path: { callId: attempt.source.callId },
                body: attempt.body,
                ...EXPECTED_MUTATION_ERRORS,
                throwOnError: true,
                signal: AbortSignal.timeout(20_000)
            });
            const data = dataFrom(response);
            if (data?.outcome === 'accepted') {
                status = 'accepted';
                message = 'Transfer accepted. The destination may still be ringing.';
            } else if (data?.outcome === 'rejected') {
                status = 'rejected';
                message = 'The transfer was not accepted. The current call has been refreshed.';
            }
        } catch (err) {
            const httpStatus = apiErrorStatus(err);
            if (!replay && httpStatus !== undefined && [400, 403, 404, 409].includes(httpStatus)) {
                status = 'rejected';
                message = 'The transfer was not admitted. The call or your access may have changed; review the refreshed call.';
            }
        }
        if (key !== this.key(attempt.source)) return;
        this.put(key, { ...attempt, status, message });
        // Preserve this acknowledgement long enough for the open modal to report it. The next
        // fresh interaction can retire it once the exact immutable source is no longer present.
        await this.refreshAndRetire(key);
    }
}

export const deskCallTransferMgr = new DeskCallTransferManager();
